import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { FaxQueries } from '@frontend/api-fax';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { useFeatureAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { createNavItem, createSubNavItems } from '../nav-item-utils';

export const useFaxNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { hasAccess: hasFaxAccess } = useFeatureAccess('fax-2-0', Permission.FAX_INBOX_MANAGE);

  const faxBlockFeatureFlag = getFeatureFlagValue('fax-2-0-block');
  const faxArchiveFeatureFlag = getFeatureFlagValue('fax-2-0-archive');

  const { data: faxUnreadCount } = FaxQueries.useCountFaxes(selectedLocationIds, {
    enabled: hasFaxAccess && selectedLocationIds.length > 0,
  });
  const { data: faxDraftUnreadCount } = FaxDraftQueries.useGetDraftCount(selectedLocationIds[0], selectedLocationIds, {
    enabled: hasFaxAccess && selectedLocationIds.length > 0 && !!selectedLocationIds[0],
  });

  const subNavItems = useMemo(() => {
    const items = [
      {
        count: faxUnreadCount?.faxCount,
        label: t('Fax Inbox'),
        relativePath: '/inbox',
        trackingId: `${FaxPrefixes.Nav}-inbox`,
        description:
          'Central location for all incoming and outgoing faxes. View, tag, archive, and further manage received fax documents in a single interface.',
      },
      {
        label: t('Drafts'),
        relativePath: '/drafts',
        count: faxDraftUnreadCount?.count,
        isPassiveCount: true,
        trackingId: `${FaxPrefixes.Nav}-drafts`,
        description: 'Collection of unsent or paused fax transmissions. Edit details or send these faxes when ready.',
      },
      ...(faxArchiveFeatureFlag
        ? [
            {
              label: t('Archived'),
              relativePath: '/archived',
              count: faxUnreadCount?.archivedFaxCount,
              isPassiveCount: true,
              trackingId: `${FaxPrefixes.Nav}-archived`,
              description:
                'Archived fax history for record-keeping. Preserves older fax communications outside the main inbox.',
            },
          ]
        : []),
      {
        label: t('Fax Contacts'),
        relativePath: '/contacts',
        trackingId: `${FaxPrefixes.Nav}-contacts`,
        description:
          'Manage contact information specifically for faxing. Add or remove fax numbers and relevant details.',
      },
      ...(faxBlockFeatureFlag
        ? [
            {
              count: faxUnreadCount?.blockedFaxCount,
              isPassiveCount: true,
              label: t('Blocked'),
              relativePath: '/blocked',
              trackingId: `${FaxPrefixes.Nav}-blocked`,
              description: 'List of blocked fax numbers to prevent unwanted inbound or outbound fax traffic.',
            },
          ]
        : []),
      {
        label: t('Send New Fax'),
        relativePath: '/drafts/new',
        show: false,
        trackingId: `${FaxPrefixes.Nav}-send-new-fax`,
        description:
          'Initiate a new fax transmission by uploading documents, selecting recipients, and adding a cover sheet.',
      },
      {
        label: t('Send From Template'),
        relativePath: '/drafts/new-from-template',
        show: false,
        trackingId: `${FaxPrefixes.Nav}-send-from-template`,
        description:
          'Quickly prepare a new fax using predefined templates to streamline repetitive or common transmissions.',
      },
    ];
    return createSubNavItems(items);
  }, [t, faxUnreadCount, faxDraftUnreadCount, faxArchiveFeatureFlag, faxBlockFeatureFlag]);

  return useMemo(
    () =>
      createNavItem('fax', {
        iconName: 'fax',
        label: t('Fax'),
        root: '/fax',
        show: hasFaxAccess,
        hasCount: !!faxUnreadCount?.faxCount,
        subNavItems,
        settings: { path: '/fax', label: '' },
      }),
    [t, faxUnreadCount?.faxCount, hasFaxAccess, subNavItems]
  );
};
